import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useTranslation } from 'modules/i18n';

import translation from './statusCell.translation';

export enum StatusType {
  Task,
  Transaction,
}

interface Props {
  isError: boolean;
  type: StatusType;
}

export function Status({ isError, type }: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);

  const message: { error: string; success: string } = useMemo(() => {
    switch (type) {
      case StatusType.Task:
        return {
          error: t(keys.paused),
          success: t(keys.active),
        };
      case StatusType.Transaction:
      default:
        return {
          error: t(keys.failed),
          success: t(keys.success),
        };
    }
  }, [keys, t, type]);

  return (
    <Typography
      className={cx(classes.status, !isError && classes.activeStatus)}
      variant="body3"
      component="div"
    >
      {t(isError ? message.error : message.success)}
    </Typography>
  );
}

const useStyles = makeStyles()(theme => ({
  status: {
    '&&': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 24,
      width: 'max-content',
      padding: theme.spacing(0, 2),
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.background.default,
      color: theme.palette.grey[600],

      '&:before': {
        content: '""',
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: theme.palette.error.main,
        marginRight: theme.spacing(1),
      },
    },
  },
  activeStatus: {
    '&&:before': {
      backgroundColor: theme.palette.success.main,
    },
  },
}));
